import { useState, useEffect } from 'react';
import {
  PlusIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { 
  FeedStock,
  CreateFeedStockDto,
  UpdateFeedStockDto,
  FeedType,
  FeedStockUnit,
  FeedStockNutritionalInfo
} from '../api/feedStock';
import { Supplier, SupplierType, suppliersApi, SupplierStatus, CreateSupplierDto } from '../api/suppliers';
import SupplierSelector from './SupplierSelector';
import NutritionalInfoForm from './NutritionalInfoForm';
import DecimalInput from './ui/DecimalInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface FeedStockFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFeedStockDto | UpdateFeedStockDto) => Promise<void>;
  feedStock?: FeedStock | null;
  mode: 'create' | 'edit';
}

interface FormData {
  name: string;
  type: FeedType;
  quantity: number;
  unit: FeedStockUnit;
  unitPrice: number;
  purchaseDate: string;
  expirationDate: string;
  supplier: string;
  supplierId: string;
  supplierContact: string;
  batchNumber: string;
  location: string;
  storageConditions: string;
  description: string;
  notes: string;
  minimumQuantity: number;
  reorderPoint: number;
  nutritionalInfo: FeedStockNutritionalInfo;
}

const initialFormData: FormData = {
  name: '',
  type: FeedType.OTHER,
  quantity: 0,
  unit: FeedStockUnit.KG,
  unitPrice: 0,
  purchaseDate: new Date().toISOString().split('T')[0],
  expirationDate: '',
  supplier: '',
  supplierId: '',
  supplierContact: '',
  batchNumber: '',
  location: '',
  storageConditions: '',
  description: '',
  notes: '',
  minimumQuantity: 0,
  reorderPoint: 0,
  nutritionalInfo: {
    // Macronutrients
    protein: undefined,
    fat: undefined,
    fiber: undefined,
    carbohydrate: undefined,
    moisture: undefined,
    calories: undefined,
    
    // Major minerals
    calcium: undefined,
    phosphorus: undefined,
    magnesium: undefined,
    potassium: undefined,
    sodium: undefined,
    
    // Trace minerals
    iron: undefined,
    zinc: undefined,
    manganese: undefined,
    copper: undefined,
    selenium: undefined,
    
    // Fat-soluble vitamins
    vitaminA: undefined,
    vitaminD3: undefined,
    vitaminE: undefined,
    vitaminK: undefined,
    
    // Water-soluble vitamins
    thiamine: undefined,
    riboflavin: undefined,
    niacin: undefined,
    pantothenicAcid: undefined,
    pyridoxine: undefined,
    biotin: undefined,
    folicAcid: undefined,
    vitaminB12: undefined,
    choline: undefined,
    
    // Analysis metadata
    guaranteed: undefined,
    analysisDate: undefined,
    analysisMethod: undefined,
    certifiedBy: undefined,
    lastUpdated: undefined,
  },
};

export default function FeedStockForm({ 
  isOpen, 
  onClose, 
  onSubmit, 
  feedStock, 
  mode 
}: FeedStockFormProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCreateSupplierModal, setShowCreateSupplierModal] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');

  // Reset form when modal opens/closes or feedStock changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && feedStock) {
        setFormData({
          name: feedStock.name || '',
          type: feedStock.type || FeedType.OTHER,
          quantity: feedStock.quantity || 0,
          unit: feedStock.unit || FeedStockUnit.KG,
          unitPrice: feedStock.unitPrice || 0,
          purchaseDate: feedStock.purchaseDate ? feedStock.purchaseDate.split('T')[0] : new Date().toISOString().split('T')[0],
          expirationDate: feedStock.expirationDate ? feedStock.expirationDate.split('T')[0] : '',
          supplier: feedStock.supplier || '',
          supplierId: (feedStock as any).supplierId || '',
          supplierContact: feedStock.supplierContact || '',
          batchNumber: feedStock.batchNumber || '',
          location: feedStock.location || '',
          storageConditions: feedStock.storageConditions || '',
          description: feedStock.description || '',
          notes: feedStock.notes || '',
          minimumQuantity: feedStock.minimumQuantity || 0,
          reorderPoint: feedStock.reorderPoint || 0,
          nutritionalInfo: {
            // Macronutrients
            protein: feedStock.nutritionalInfo?.protein,
            fat: feedStock.nutritionalInfo?.fat,
            fiber: feedStock.nutritionalInfo?.fiber,
            carbohydrate: feedStock.nutritionalInfo?.carbohydrate,
            moisture: feedStock.nutritionalInfo?.moisture,
            calories: feedStock.nutritionalInfo?.calories,
            
            // Major minerals
            calcium: feedStock.nutritionalInfo?.calcium,
            phosphorus: feedStock.nutritionalInfo?.phosphorus,
            magnesium: feedStock.nutritionalInfo?.magnesium,
            potassium: feedStock.nutritionalInfo?.potassium,
            sodium: feedStock.nutritionalInfo?.sodium,
            
            // Trace minerals
            iron: feedStock.nutritionalInfo?.iron,
            zinc: feedStock.nutritionalInfo?.zinc,
            manganese: feedStock.nutritionalInfo?.manganese,
            copper: feedStock.nutritionalInfo?.copper,
            selenium: feedStock.nutritionalInfo?.selenium,
            
            // Fat-soluble vitamins
            vitaminA: feedStock.nutritionalInfo?.vitaminA,
            vitaminD3: feedStock.nutritionalInfo?.vitaminD3,
            vitaminE: feedStock.nutritionalInfo?.vitaminE,
            vitaminK: feedStock.nutritionalInfo?.vitaminK,
            
            // Water-soluble vitamins
            thiamine: feedStock.nutritionalInfo?.thiamine,
            riboflavin: feedStock.nutritionalInfo?.riboflavin,
            niacin: feedStock.nutritionalInfo?.niacin,
            pantothenicAcid: feedStock.nutritionalInfo?.pantothenicAcid,
            pyridoxine: feedStock.nutritionalInfo?.pyridoxine,
            biotin: feedStock.nutritionalInfo?.biotin,
            folicAcid: feedStock.nutritionalInfo?.folicAcid,
            vitaminB12: feedStock.nutritionalInfo?.vitaminB12,
            choline: feedStock.nutritionalInfo?.choline,
            
            // Analysis metadata
            guaranteed: feedStock.nutritionalInfo?.guaranteed,
            analysisDate: feedStock.nutritionalInfo?.analysisDate,
            analysisMethod: feedStock.nutritionalInfo?.analysisMethod,
            certifiedBy: feedStock.nutritionalInfo?.certifiedBy,
            lastUpdated: feedStock.nutritionalInfo?.lastUpdated,
          },
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [isOpen, feedStock, mode]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (formData.quantity < 0) {
      newErrors.quantity = 'Quantity cannot be negative';
    }
    
    if (formData.unitPrice < 0) {
      newErrors.unitPrice = 'Unit price cannot be negative';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const submissionData = {
        ...formData,
        quantity: Number(formData.quantity),
        unitPrice: Number(formData.unitPrice),
        minimumQuantity: Number(formData.minimumQuantity),
        reorderPoint: Number(formData.reorderPoint),
        nutritionalInfo: formData.nutritionalInfo
      };

      
      await onSubmit(submissionData);
      
      toast.success(mode === 'create' ? 'Feed stock added successfully' : 'Feed stock updated successfully');
      onClose();
    } catch (error: any) {
      console.error('Error submitting feed stock:', error);
      toast.error(error.message || 'Failed to save feed stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSupplierChange = (supplierId: string, supplier?: Supplier) => {
    setFormData(prev => ({
      ...prev,
      supplierId,
      supplier: supplier?.name || '',
      supplierContact: supplier?.contactPerson || ''
    }));
  };

  const handleNutritionalInfoChange = (field: keyof FeedStockNutritionalInfo, value: any) => {
    setFormData(prev => ({
      ...prev,
      nutritionalInfo: { ...prev.nutritionalInfo, [field]: value }
    }));
  };

  const handleCreateSupplier = () => {
    setShowCreateSupplierModal(true);
  };

  const handleQuickCreateSupplier = async () => {
    if (!newSupplierName.trim()) return;

    try {
      const supplierData: CreateSupplierDto = {
        name: newSupplierName,
        code: newSupplierName.toUpperCase().replace(/\s+/g, '_'),
        type: SupplierType.FEED,
        status: SupplierStatus.ACTIVE,
        contactPerson: '',
        address: '',
        description: `Quick created from feed stock form`
      };

      const response = await suppliersApi.createSupplier(supplierData);
      toast.success('Supplier created successfully');
      
      // Set the newly created supplier
      handleSupplierChange(response.id, response);
      
      setShowCreateSupplierModal(false);
      setNewSupplierName('');
    } catch (error: any) {
      console.error('Failed to create supplier:', error);
      toast.error('Failed to create supplier');
    }
  };

  return (
    <>
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-6xl">
      <BrandModalHeader
        title={mode === 'create' ? 'Add New Feed Stock Item' : `Edit ${feedStock?.name || 'Feed Stock Item'}`}
        subtitle={mode === 'create'
          ? 'Create a new feed stock item for your inventory'
          : 'Update the details of this feed stock item'}
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="max-h-[70vh] overflow-y-auto">
                    <div className="space-y-8">
                      {/* Basic Information */}
                      <div>
                        <h4 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h4>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                          <div className="lg:col-span-2">
                            <label className="block text-sm font-medium text-slate-700">
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              required
                              className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                errors.name ? 'border-red-300' : ''
                              }`}
                              value={formData.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              placeholder="e.g., Premium Canary Mix"
                            />
                            {errors.name && (
                              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700">
                              Feed Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              required
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.type}
                              onChange={(e) => handleInputChange('type', e.target.value as FeedType)}
                            >
                              {Object.values(FeedType).map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>

                          <DecimalInput
                            label="Quantity"
                            value={formData.quantity}
                            onChange={(value) => handleInputChange('quantity', value)}
                            required
                            min={0}
                            error={errors.quantity}
                          />

                          <div>
                            <label className="block text-sm font-medium text-slate-700">
                              Unit <span className="text-red-500">*</span>
                            </label>
                            <select
                              required
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.unit}
                              onChange={(e) => handleInputChange('unit', e.target.value as FeedStockUnit)}
                            >
                              {Object.values(FeedStockUnit).map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                              ))}
                            </select>
                          </div>

                          <DecimalInput
                            label="Unit Price (ZAR)"
                            value={formData.unitPrice}
                            onChange={(value) => handleInputChange('unitPrice', value)}
                            required
                            min={0}
                            grouped
                            fractionDigits={2}
                            placeholder="0.00"
                            error={errors.unitPrice}
                          />

                          <div>
                            <label className="block text-sm font-medium text-slate-700">
                              Total Value
                            </label>
                            <input
                              type="text"
                              disabled
                              className="mt-1 block w-full rounded-md border-gray-300 bg-slate-50/80 shadow-sm sm:text-sm text-slate-600 font-medium"
                              value={`R${(formData.quantity * formData.unitPrice).toFixed(2)}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Supplier and Batch Information */}
                      <div>
                        <h4 className="text-lg font-medium text-slate-900 mb-4">Supplier & Batch Information</h4>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-slate-700">Supplier</label>
                            <SupplierSelector
                              value={formData.supplierId}
                              onChange={handleSupplierChange}
                              onSupplierContactChange={(contact) => handleInputChange('supplierContact', contact)}
                              filterByType={SupplierType.FEED}
                              placeholder="Select a feed supplier..."
                              onCreateSupplier={handleCreateSupplier}
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700">Batch Number</label>
                            <input
                              type="text"
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.batchNumber}
                              onChange={(e) => handleInputChange('batchNumber', e.target.value)}
                              placeholder="e.g., CN2024-045"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700">Purchase Date</label>
                            <input
                              type="date"
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.purchaseDate}
                              onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700">Expiration Date</label>
                            <input
                              type="date"
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.expirationDate}
                              onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Storage and Inventory Management */}
                      <div>
                        <h4 className="text-lg font-medium text-slate-900 mb-4">Storage & Inventory Management</h4>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                          <div>
                            <label className="block text-sm font-medium text-slate-700">Storage Location</label>
                            <input
                              type="text"
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.location}
                              onChange={(e) => handleInputChange('location', e.target.value)}
                              placeholder="e.g., Feed Room A, Shelf 3"
                            />
                          </div>

                          <DecimalInput
                            label="Minimum Quantity"
                            value={formData.minimumQuantity}
                            onChange={(value) => handleInputChange('minimumQuantity', value)}
                            min={0}
                          />

                          <DecimalInput
                            label="Reorder Point"
                            value={formData.reorderPoint}
                            onChange={(value) => handleInputChange('reorderPoint', value)}
                            min={0}
                          />

                          <div className="lg:col-span-2 xl:col-span-3">
                            <label className="block text-sm font-medium text-slate-700">Storage Conditions</label>
                            <input
                              type="text"
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.storageConditions}
                              onChange={(e) => handleInputChange('storageConditions', e.target.value)}
                              placeholder="e.g., Cool, dry place. Temperature below 25\u00b0C"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Description and Notes */}
                      <div>
                        <h4 className="text-lg font-medium text-slate-900 mb-4">Description & Notes</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700">Description</label>
                            <textarea
                              rows={3}
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              placeholder="Brief description of the feed product..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700">Notes</label>
                            <textarea
                              rows={3}
                              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                              value={formData.notes}
                              onChange={(e) => handleInputChange('notes', e.target.value)}
                              placeholder="Additional notes or observations..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Nutritional Information */}
                      <div>
                        <h4 className="text-lg font-medium text-slate-900 mb-4">Nutritional Information</h4>
                        <NutritionalInfoForm
                          nutritionalInfo={formData.nutritionalInfo}
                          onChange={handleNutritionalInfoChange}
                          errors={errors}
                          feedName={formData.name}
                          feedType={formData.type}
                          unit={formData.unit}
                          description={formData.description}
                        />
                      </div>
                    </div>

        </BrandModalBody>
        <BrandModalFooter>
                    <button
                      type="button"
                      className="btn btn-secondary text-sm"
                      onClick={onClose}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                      <button
                        type="submit"
                        className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {mode === 'create' ? 'Creating...' : 'Updating...'}
                          </>
                        ) : (
                          mode === 'create' ? 'Create Feed Stock' : 'Update Feed Stock'
                        )}
                      </button>
        </BrandModalFooter>
      </form>
    </BrandModal>

      <BrandModal
        open={showCreateSupplierModal}
        onClose={() => {
          setShowCreateSupplierModal(false);
          setNewSupplierName('');
        }}
        maxWidthClass="max-w-lg"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Quick Create Supplier" onClose={() => {
          setShowCreateSupplierModal(false);
          setNewSupplierName('');
        }} />
        <BrandModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Enter the supplier name to quickly create a new feed supplier.
          </p>
          <label className="block text-sm font-medium text-slate-700">Supplier Name</label>
          <input
            type="text"
            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            value={newSupplierName}
            onChange={(e) => setNewSupplierName(e.target.value)}
            placeholder="Enter supplier name..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleQuickCreateSupplier();
              }
            }}
          />
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => {
              setShowCreateSupplierModal(false);
              setNewSupplierName('');
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={handleQuickCreateSupplier}
          >
            Create
          </button>
        </BrandModalFooter>
      </BrandModal>
    </>
  );
}